'use client';

import { createStore } from 'zustand';
import { createContext, useRef, useEffect } from 'react';
import { useRouter, useSearchParams, useParams } from 'next/navigation';
import { TSeatCoverDataDB } from '@/lib/db/seat-covers';
import { IProductData, TPathParams, TQueryParams } from '@/utils';
import { getLatestYear, isFullSet, getSelectedProductIndex, filterProductsBySearchParams } from '@/lib/utils';
import { TProductReviewSummary, TReviewData } from '@/lib/types/review';
import { bodyTrimCustomSortOrder, seatCoverDisplayColorCustomSortOrder } from '@/lib/constants';
import { sortByYearGenerationAndSubmodel, sortByDisplayColor } from '@/lib/utils/sorting';
import { getSequentialAutoSelections } from '@/lib/utils/auto-selection';
type SeatCoverSelectionStore = ReturnType<typeof createSeatCoverSelectionStore>;
export type TQuery = {
  type: string;
  coverType: string;
  year: string;
  make: string;
  model: string;
  submodel: string; // Get rid of this in the future
  secondSubmodel: string; // Get rid of this in the future
  thirdSubmodel: string; // Get rid of this in the future
  submodel1: string;
  submodel2: string;
  submodel3: string;
  parent_generation: string;
  modelDetail: string;
  cab: string;
  color: string;
  seatSet: string;
  seatStyle: string;
};
interface SelectionConfig {
  mode: 'single' | 'multiple';
  maxSelections?: number; // for multiple
  minSelections?: number; // for multiple
}
interface ISeatCoverCoverProps {
  modelData: TSeatCoverDataDB[];
  reviewData: TReviewData[];
  reviewDataSummary: TProductReviewSummary;
  reviewImages: TReviewData[];
}
export interface ISeatCoverCoverSelectionState extends ISeatCoverCoverProps {
  selectedProduct: TSeatCoverDataDB;
  setSelectedProduct: (newProduct: TSeatCoverDataDB) => void;
  selectedColor: string;
  setSelectedColor: (color: string) => void;
  query: TQuery;
  setQuery: (newQuery: Partial<TQuery>) => void;
  isComplete: boolean;
  setReviewData: (newReviewData: TReviewData[]) => void;
  addReviewData: (data: TReviewData[]) => void;
  setReviewDataSummary: (newReviewDataSummary: TProductReviewSummary) => void;
  reviewImageTracker: Record<string, boolean>;
  setReviewImageTracker: (newImageTracker: Record<string, boolean>) => void;
  availableColors: string[];
  selectedSetDisplay?: string;
  setAvailableColors: (availableColors: string[]) => void;
  setSelectedSetDisplay?: (selectedSet: string) => void;
  selectedYearGeneration: string;
  setSelectedYearGeneration: (year: string) => void;
  selectedBodyTrim: string;
  setSelectedBodyTrim: (bodyTrim: string) => void;
  selectedBedSize: string;
  setSelectedBedSize: (bedSize: string) => void;
  selectedMirror: string;
  setSelectedMirror: (mirror: string) => void;
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  selectedModelDetail: string;
  setSelectedModelDetail: (modelDetail: string) => void;
  selectedSeatTypeOption: string;
  setSelectedSeatTypeOption: (seatTypeOption: string) => void;
  selectedSeatSubtype: string;
  setSelectedSeatSubtype: (seatSubtype: string) => void;
  selectedProducts: TSeatCoverDataDB[];
  setSelectedProducts: (products: TSeatCoverDataDB[]) => void;
  addSelectedProduct: (product: TSeatCoverDataDB) => void;
  removeSelectedProduct: (productId: number) => void;
  clearSelectedProducts: () => void;
  selectionConfig: SelectionConfig;
  setSelectionConfig: (config: SelectionConfig) => void;
  toggleProductSelection: (product: TSeatCoverDataDB) => void;
  updateAllSelectedProducts: (matcher: (product: TSeatCoverDataDB) => boolean, updater: (product: TSeatCoverDataDB) => TSeatCoverDataDB) => void;
  updateProductsColor: (newColor: string) => void;
  updateProductSeatType: (productId: number, seatTypeOption: string, seatSubtype: string) => void;
}
type SeatCoverSelectionStoreParams = {
  modelData: TSeatCoverDataDB[];
  params: TPathParams;
  searchParams: TQueryParams | undefined;
  initialReviewData: TReviewData[];
  initialReviewDataSummary: TProductReviewSummary;
  initialReviewImages: TReviewData[];
  isComplete: boolean;
};
const createSeatCoverSelectionStore = ({
  modelData,
  params,
  searchParams,
  initialReviewData,
  initialReviewDataSummary,
  initialReviewImages,
  isComplete = false
}: SeatCoverSelectionStoreParams) => {
  const customerSelectedYear = searchParams?.year;
  const sortInitialDataByDisplayColor = sortByDisplayColor(modelData, seatCoverDisplayColorCustomSortOrder);
  const sortedInitialDataByYearGenerationAndSubmodel: IProductData[] = isComplete ? sortByYearGenerationAndSubmodel(sortInitialDataByDisplayColor, bodyTrimCustomSortOrder) : modelData;

  // Detect if third row seats are available
  const hasThirdRow = modelData.some(product => ['third', 'third row'].includes(product.display_set?.toLowerCase?.().trim?.() || ''));
  const finalModelData = modelData.length === 0 ? updateProductPreorderDetailsWithDynamicLogic(modelData) : modelData;
  const reviewImageTracker: Record<string, boolean> = {};
  initialReviewData.forEach(reviewData => {
    !!reviewData.review_image && reviewData.review_image.split(',').map(imageUrl => {
      if (!reviewImageTracker[imageUrl]) {
        reviewImageTracker[imageUrl] = true;
      }
    });
  });
  const initialQueryState = {
    year: params?.year && customerSelectedYear || '',
    type: params?.productType ?? '',
    coverType: params?.coverType ?? 'leather',
    make: params?.make ?? '',
    model: params?.model ?? '',
    submodel: searchParams?.submodel ?? '',
    secondSubmodel: searchParams?.submodel2 ?? '',
    thirdSubmodel: searchParams?.submodel3 ?? '',
    submodel1: searchParams?.submodel ?? '',
    submodel2: searchParams?.submodel2 ?? '',
    submodel3: searchParams?.submodel3 ?? '',
    parent_generation: '',
    modelDetail: searchParams?.submodel ?? '',
    fitment: searchParams?.fitment ?? '',
    cab: searchParams?.cab ?? '',
    color: searchParams?.color ?? '',
    set: searchParams?.set ?? '',
    style: searchParams?.style ?? '',
    year: searchParams?.year ?? ''
  };
  const selectedProductIndex = getSelectedProductIndex(sortedInitialDataByYearGenerationAndSubmodel, searchParams);
  const productEndYear = getLatestYear(sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]);
  let finalYear: number | null = Number(customerSelectedYear);

  // Handle invalid year values - fallback to null (blank)
  if (isNaN(finalYear) || finalYear <= 0) {
    finalYear = null;
  } else if (productEndYear && finalYear > productEndYear) {
    finalYear = productEndYear;
  }
  const firstAvailableSet = sortedInitialDataByYearGenerationAndSubmodel.filter(seatCover => isFullSet(seatCover?.display_set ?? '') === isFullSet(sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_set ?? ''));
  const firstAvailableSeat = sortedInitialDataByYearGenerationAndSubmodel.find(item => item.display_set === 'Front Seat');
  const availableColors = new Set(['black', 'gray', 'beige', 'dark gray', 'red', 'pink', 'white', 'dark brown', 'brown', 'wine red', 'woodland black', 'woodland beige', 'woodland green', 'grass black', 'grass brown']);
  const firstAvaiableColor = firstAvailableSet.map(seatCover => seatCover?.display_color?.toLowerCase() ?? '').filter(color => availableColors.has(color));

  // Auto-select submodels if only one option is available
  const selectedColor = sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_color ?? 'Black';
  const selectedYearValue = finalYear?.toString() ?? '';
  const autoSelections = getSequentialAutoSelections(sortedInitialDataByYearGenerationAndSubmodel, ['submodel1', 'submodel2', 'submodel3'], {
    display_color: selectedColor,
    year: selectedYearValue
  });
  return createStore<ISeatCoverCoverSelectionState>()((set, get) => ({
    modelData: sortedInitialDataByYearGenerationAndSubmodel,
    query: initialQueryState,
    setQuery: (newQuery: Partial<TQuery>) => {
      set(state => ({
        ...state,
        query: {
          ...state.query,
          ...newQuery
        }
      }));
    },
    selectedProduct: sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex],
    setSelectedProduct: (newProduct: TSeatCoverDataDB) => {
      set(state => {
        const {
          selectionConfig
        } = state;

        // In single selection mode, always replace with new product
        if (selectionConfig.mode === 'single') {
          return {
            selectedProduct: newProduct,
            featuredImage: newProduct?.product?.split(',')[0] ?? '',
            selectedProducts: [newProduct]
          };
        }

        // In multiple selection mode, update selectedProduct but preserve selectedProducts array
        // unless this is the first product being selected
        const updatedSelectedProducts = state.selectedProducts.length === 0 ? [newProduct] : state.selectedProducts;
        return {
          selectedProduct: newProduct,
          featuredImage: newProduct?.product?.split(',')[0] ?? '',
          selectedProducts: updatedSelectedProducts
        };
      });
    },
    selectedColor: sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_color ?? 'Black',
    setSelectedColor: (newColor: string) => set(() => ({
      selectedColor: newColor
    })),
    isComplete,
    reviewImages: initialReviewImages,
    reviewImageTracker,
    setReviewImageTracker: (newImageTracker: Record<string, boolean>) => {
      set(() => ({
        reviewImageTracker: newImageTracker
      }));
    },
    reviewData: initialReviewData,
    setReviewData: (newReviewData: TReviewData[]) => {
      set(() => ({
        reviewData: newReviewData
      }));
    },
    addReviewData: (data: TReviewData[]) => set(state => ({
      reviewData: [...state.reviewData, ...data]
    })),
    reviewDataSummary: initialReviewDataSummary,
    setReviewDataSummary: (newReviewDataSummary: TProductReviewSummary) => {
      set(() => ({
        reviewDataSummary: newReviewDataSummary
      }));
    },
    setReviewsWithImages: (newReviewImages: TReviewData[]) => {
      set(() => ({
        reviewImages: newReviewImages
      }));
    },
    selectedSetDisplay: isComplete ? isFullSet(sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_set ?? '') : 'front',
    setSelectedSetDisplay: (selectedSet: string) => {
      set(() => ({
        selectedSetDisplay: selectedSet
      }));
    },
    availableColors: firstAvaiableColor,
    setAvailableColors: (newAvailableColors: string[]) => set(() => ({
      availableColors: newAvailableColors
    })),
    selectedYearGeneration: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.year_generation ?? '' : '',
    setSelectedYearGeneration: (year: string) => set(() => ({
      selectedYearGeneration: year
    })),
    selectedBodyTrim: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel1 ?? '' : autoSelections.submodel1 || '',
    setSelectedBodyTrim: (bodyTrim: string) => set(() => ({
      selectedBodyTrim: bodyTrim
    })),
    selectedBedSize: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel2 ?? '' : autoSelections.submodel2 || '',
    setSelectedBedSize: (bedSize: string) => set(() => ({
      selectedBedSize: bedSize
    })),
    selectedMirror: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel3 ?? '' : autoSelections.submodel3 || '',
    setSelectedMirror: (mirror: string) => set(() => ({
      selectedMirror: mirror
    })),
    selectedYear: finalYear?.toString() ?? '',
    setSelectedYear: (year: string) => set(() => ({
      selectedYear: year
    })),
    selectedModelDetail: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.model_detail ?? '' : '',
    setSelectedModelDetail: (modelDetail: string) => set(() => ({
      selectedModelDetail: modelDetail
    })),
    selectedSeatTypeOption: '',
    setSelectedSeatTypeOption: (seatTypeOption: string) => set(() => ({
      selectedSeatTypeOption: seatTypeOption
    })),
    selectedSeatSubtype: '',
    setSelectedSeatSubtype: (seatSubtype: string) => set(() => ({
      selectedSeatSubtype: seatSubtype
    })),
    selectedProducts: [] as TSeatCoverDataDB[],
    setSelectedProducts: products => set({
      selectedProducts: products
    }),
    addSelectedProduct: product => {
      const {
        selectedProducts,
        selectionConfig
      } = get();
      if (selectionConfig.mode === 'single') {
        set({
          selectedProducts: [product],
          selectedProduct: product
        });
        return;
      }

      // Check if product already exists in selectedProducts (prevent duplicates)
      const isAlreadySelected = selectedProducts.some(p => p.id === product.id);
      if (isAlreadySelected) {
        return;
      }
      if (selectionConfig.maxSelections && selectedProducts.length >= selectionConfig.maxSelections) {
        return;
      }
      const newSelectedProducts = [...selectedProducts, product];
      set({
        selectedProducts: newSelectedProducts
      });
    },
    removeSelectedProduct: productId => {
      const {
        selectedProducts,
        selectionConfig
      } = get();
      if (selectionConfig.mode === 'single' || selectedProducts.length <= selectionConfig.minSelections!) {
        return;
      }
      const newSelected = selectedProducts.filter(p => p.id !== productId);
      if (selectionConfig.minSelections && newSelected.length < selectionConfig.minSelections) {
        return;
      }
      set({
        selectedProducts: newSelected
      });
    },
    clearSelectedProducts: () => {
      const {
        selectedProducts
      } = get();
      if (selectedProducts.length > 0) {
        set({
          selectedProducts: []
        });
      }
    },
    selectionConfig: {
      mode: hasThirdRow ? 'multiple' : 'single',
      productType: 'seat-covers',
      minSelections: 1,
      maxSelections: hasThirdRow ? 3 : 1
    },
    setSelectionConfig: config => {
      set({
        selectionConfig: config
      });
    },
    replaceSelectedProducts: (newProducts: TSeatCoverDataDB[]) => set(() => ({
      selectedProducts: newProducts
    })),
    toggleProductSelection: product => {
      const {
        selectedProducts,
        addSelectedProduct,
        removeSelectedProduct
      } = get();
      const isSelected = selectedProducts.some(p => p.id === product.id);
      if (isSelected) {
        removeSelectedProduct(product.id);
      } else {
        addSelectedProduct(product);
      }
    },
    updateAllSelectedProducts: (matcher, updater) => {
      const {
        selectedProducts
      } = get();
      const updated = selectedProducts.map(product => matcher(product) ? updater(product) : product);
      set({
        selectedProducts: updated
      });
    },
    updateProductsColor: (newColor: string) => set(state => ({
      selectedProducts: state.selectedProducts.map(product => {
        const matchingProduct = state.modelData.find(p => p.display_set === product.display_set && p.submodel1 === product.submodel1 && p.display_color?.toLowerCase() === newColor.toLowerCase());
        return matchingProduct || product;
      })
    })),
    updateProductSeatType: (productId: number, seatTypeOption: string, seatSubtype: string) => set(state => ({
      selectedProducts: state.selectedProducts.map(product => {
        if (product.id === productId) {
          // Find matching product with the new seat type
          const matchingProduct = state.modelData.find(p => p.display_set === product.display_set && p.submodel1 === product.submodel1 && p.submodel2 === product.submodel2 && p.submodel3 === product.submodel3 && p.year_generation === product.year_generation && p.model_detail === product.model_detail && p.display_color === product.display_color && p.seat_type_option === seatTypeOption && p.seat_subtype === seatSubtype);
          return matchingProduct || {
            ...product,
            seat_type_option: seatTypeOption,
            seat_subtype: seatSubtype
          };
        }
        return product;
      }),
      // Update global seat type selection for the UI
      selectedSeatTypeOption: seatTypeOption,
      selectedSeatSubtype: seatSubtype
    }))
  }));
};
export const SeatCoverSelectionContext = createContext<SeatCoverSelectionStore | null>(null);
const SeatCoverSelectionProvider = ({
  children,
  initialState
}: {
  children: React.ReactNode;
  initialState: any;
}) => {
  const {
    modelData,
    params,
    reviewData,
    reviewDataSummary,
    reviewImages
  } = initialState;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathParams = useParams<TPathParams>();

  // Convert URLSearchParams to object
  const searchParamsObj: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    searchParamsObj[key] = value;
  });
  if (modelData.length === 0) {
    router.push('/404');
  }

  // Client-side validation and filtering
  const filteredData = filterProductsBySearchParams(modelData, searchParamsObj);

  // Check if search params result in valid selection
  const hasSearchParams = Object.values(searchParamsObj).some(param => param && param.trim() !== '');
  const onlyYearParam = searchParamsObj.year && Object.entries(searchParamsObj).filter(([key, value]) => key !== 'year' && value && value.trim() !== '').length === 0;
  const isValidSelection = !hasSearchParams || onlyYearParam || filteredData.length === 1;
  const shouldCleanUrl = hasSearchParams && !onlyYearParam && (filteredData.length === 0 || filteredData.length > 1);

  // Clean URL if invalid params
  useEffect(() => {
    if (shouldCleanUrl) {
      const basePath = `/seat-covers/${pathParams?.coverType}/${pathParams?.make || ''}/${pathParams?.model || ''}`;
      // console.log('@@@ router.replace (SeatCoverContext)', { basePath, stack: new Error().stack });
      router.replace(basePath, {
        scroll: false
      });
    }
  }, [shouldCleanUrl, router, pathParams]);
  const finalIsComplete = filteredData.length === 1;
  const store = useRef(createSeatCoverSelectionStore({
    modelData: modelData,
    params,
    searchParams: searchParamsObj,
    initialReviewData: reviewData as TReviewData[],
    initialReviewDataSummary: reviewDataSummary,
    initialReviewImages: reviewImages,
    isComplete: finalIsComplete
  })).current;
  return <SeatCoverSelectionContext.Provider value={store} data-sentry-element="unknown" data-sentry-component="SeatCoverSelectionProvider" data-sentry-source-file="SeatCoverContext.tsx">
      {children}
    </SeatCoverSelectionContext.Provider>;
};
export default SeatCoverSelectionProvider;